<template>
  <div v-if="!routeAnimating" tabindex="1">
    <div
      class="loader-container-top"
      v-if="selectedUserId in loadingMessage && loadingMessage[selectedUserId]"
    >
      <loader-vue />
    </div>
    <div
      ref="messageContainer"
      class="message-box"
      v-if="currentUserMessages && currentUserMessages.length > 0"
    >
      <div
        v-for="message in currentUserMessages"
        :key="message.id"
        :id="'chat-message' + message.id"
        class="all-message-container"
        :class="{
          'user-message-container': !(message.sent_by !== userId)
        }"
      >
        <!-- 1. User message - when new message is added -->
        <transition enter-active-class="zoomIn" appear mode="out-in">
          <div
            v-if="
              message.sent_by === userId &&
                'animate' in message &&
                typeof message.id === 'string'
            "
            class="user-message custom"
          >
            <span class="message-username user-username">
              {{ message.sent_time }}
            </span>
            <span class="message-content loader">
              <p class="message-chatbox" v-text="message.value"></p>
              <div
                class="loader-small"
                v-if="typeof message.id === 'string'"
              ></div>
            </span>
          </div>
        </transition>

        <!-- 2. User message - when new message is updated with the id -->
        <transition leave-active-class="zoomOut" appear mode="out-in">
          <div
            v-if="
              message.sent_by === userId &&
                'animate' in message &&
                typeof message.id === 'number'
            "
            class="user-message"
          >
            <span class="message-username user-username">
              {{ message.sent_time }}
            </span>
            <p class="message-chatbox" v-text="message.value"></p>
          </div>
        </transition>

        <!-- 3. User message - when new message is added from another logged in tab -->
        <transition
          enter-active-class="zoomIn"
          leave-active-class="zoomOut"
          appear
          mode="out-in"
        >
          <div
            v-if="message.sent_by === userId && !('animate' in message)"
            class="user-message"
          >
            <span class="message-username user-username">
              {{ message.sent_time }}
            </span>
            <p class="message-chatbox" v-text="message.value"></p>
          </div>
        </transition>

        <!-- 4. Client message -->
        <transition
          enter-active-class="zoomIn"
          leave-active-class="zoomOut"
          appear
          mode="out-in"
        >
          <div v-if="message.sent_by !== userId" class="client-message">
            <span class="message-username client-username"
              >{{
                message.firstName
                  ? `${message.lastName}, ${message.firstName}`
                  : username
              }}, {{ message.sent_time }}</span
            >
            <p class="message-chatbox" v-text="message.value"></p>
          </div>
        </transition>
      </div>
    </div>

    <!-- Down button -->
    <div class="main-unread-container" v-if="showGoDown" @click="goToBottom">
      <div class="unread" v-if="unread !== 0">{{ unread }}</div>
      <div class="arrow-container">
        <object
          class="down-arrow"
          :data="rightArrow"
          type="image/svg+xml"
        ></object>
      </div>
    </div>

    <div
      class="message-box custom"
      v-if="
        !(selectedUserId in loadingMessage && loadingMessage[selectedUserId]) &&
          !(currentUserMessages && currentUserMessages.length > 0)
      "
    >
      <p class="no-message">No messages</p>
    </div>
  </div>
</template>

<script>
import LoaderVue from "../Appointment/common/Loader";
import moment from "moment";
import { chatUsernameMixin } from "../../mixins/chatUsernameMixin";
import { nameMixin } from "../../mixins/nameMixin.js";
import { mapGetters, mapActions } from "vuex";

export default {
  mixins: [nameMixin, chatUsernameMixin],
  props: {
    goToBottomAction: Boolean,
    updateGoToBottom: Function
  },
  data() {
    return {
      readTimeout: false,
      preventUpdate: false,
      end: false,
      scrollY: 0,
      prevHeight: 0,
      showGoDown: false,
      root: false,
      rightArrow: require("../../assets/svg/right-arrow.svg")
    };
  },
  computed: {
    ...mapGetters("clients", {
      clients: "getClients"
    }),
    ...mapGetters("chat", {
      loadingMessage: "getLoadingMessage",
      personalMessages: "getMessages",
      groupMessages: "getGroupMessages",
      isGroup: "getIsGroup"
    }),
    ...mapGetters({
      routeAnimating: "getRouteAnimating"
    }),
    messages() {
      if (this.isGroup) {
        return this.groupMessages;
      }
      return this.personalMessages;
    },
    unread() {
      if (this.friends && this.selectedUserId in this.friends) {
        return this.friends[this.selectedUserId].unread;
      } else {
        return 0;
      }
    },
    currentUserMessages() {
      if (this.selectedUserId in this.messages) {
        return this.messages[this.selectedUserId].map(message => {
          const newMessage = { ...message };
          newMessage.sent_time = moment(newMessage.sent_time).calendar();
          return newMessage;
        });
      } else {
        return false;
      }
    }
  },
  methods: {
    ...mapActions("chat", {
      getMessages: "getMessages",
      readMessage: "readMessage"
    }),
    /**
     * Resize handler
     */
    resizeHandler() {
      // Root element from where we are supposed to scroll
      this.root = document.querySelector(".slide-section-main");

      // Run this function to set more defaults
      this.scrollBehaviour();

      // Compete the updated tasks
      this.updatedTasks();
    },
    /**
     * The scroll behaviour of the chat
     */
    scrollBehaviour() {
      if (!this.root) {
        return;
      }

      // Check if we have reached the bottom
      if (this.root.scrollTop >= this.scrollY - 100) {
        this.end = true; // End has been reached
        this.showGoDown = false; // Don't show the down arrow button
        this.readMessageLocal(); // Read the unread messages
        return;
      }

      if (this.root.scrollTop < this.scrollY - 100) {
        this.end = false; // Not at the end
        this.showGoDown = true; // Show the go down arrow
        this.bringOlderMessages(); // Bring new messages if we have reached the top
      }
    },
    /**
     * Bring older messages when the top has been reached
     */
    bringOlderMessages() {
      // Step 1: Check if top has been reached and no more messages are being brought in
      if (
        this.root.scrollTop === 0 &&
        !(
          this.selectedUserId in this.loadingMessage &&
          this.loadingMessage[this.selectedUserId]
        )
      ) {
        // Step 2: Set the previous height
        this.prevHeight = this.root.scrollHeight;

        // Step 3: Get the older messages
        this.getMessages(true);
      }
    },
    /**
     * Scroll to the first unread message
     */
    scrollToFirstUnreadMessage() {
      if (this.currentUserMessages.length > 0) {
        for (let i = 0; i <= this.currentUserMessages.length; i++) {
          if (
            this.currentUserMessages[i] !== undefined &&
            this.currentUserMessages[i].read_time === null &&
            this.currentUserMessages[i].sent_by === this.selectedUserId
          ) {
            var element_to_scroll_to = document.getElementById(
              "chat-message" + this.currentUserMessages[i].id
            );
            if (element_to_scroll_to === null) {
              requestAnimationFrame(this.scrollToFirstUnreadMessage);
              return;
            }
            const coordinates = element_to_scroll_to.getBoundingClientRect();
            if (coordinates.y <= this.root.scrollHeight) {
              this.scrollToLocal(
                this.root,
                0,
                coordinates.y - window.innerHeight + 120
              );
            } else {
              requestAnimationFrame(this.scrollToFirstUnreadMessage);
            }
            return;
          }
        }
        this.goToBottom(false);
      } else {
        requestAnimationFrame(this.scrollToFirstUnreadMessage);
      }
    },
    /**
     * Read the messages with a timeout
     */
    readMessageLocal() {
      if (this.readTimeout) {
        clearTimeout(this.readTimeout);
      }
      this.readTimeout = setTimeout(() => {
        this.preventUpdate = true;
        this.$nextTick(() => {
          this.readMessage();
          this.setTimeout = false;
          setTimeout(() => {
            this.preventUpdate = false;
          }, 0);
        });
      }, 100);
    },
    /**
     * When a new message is sent
     */
    handleGoDown() {
      this.end = true; // Go to the end
      this.showGoDown = false; // Don't show the down arrow
      this.updateGoToBottom(false); // Set the go down to false
    },
    /**
     * Some browsers don't support scrollTo, We use this to fix that
     */
    scrollToLocal(elem, x, y) {
      if (!("scrollTo" in elem)) {
        elem.scrollTop = y;
      } else {
        elem.scrollTo(x, y);
      }
    },
    /**
     * Go to the bottom
     */
    goToBottom(animate = true) {
      if (this.root) {
        if (!("scrollTo" in this.root)) {
          this.root.scrollTop = this.root.scrollHeight + 1000;
        } else if (animate) {
          this.root.scroll({
            top: this.root.scrollHeight + 1000,
            left: 0,
            behavior: "smooth"
          });
        } else {
          this.root.scrollTo(0, this.root.scrollHeight + 1000);
        }
      }
    },
    /**
     * There are to be done on update
     */
    updatedTasks() {
      // Step 1: If the end is true, go to the end
      if (this.end) {
        this.$nextTick(() => {
          this.goToBottom(false);
        });
      }

      // Step 2: Set the scrollY
      const scrollHeight = this.root.scrollHeight;
      this.scrollY = scrollHeight - this.root.offsetHeight;

      // Step 3: If the scroll height is less that window height read the message
      if (scrollHeight <= window.innerHeight) {
        this.readMessageLocal();
      }

      // Step 4: Maintain position if bringing older messages
      if (
        this.prevHeight !== 0 &&
        !(
          this.selectedUserId in this.loadingMessage &&
          this.loadingMessage[this.selectedUserId]
        )
      ) {
        this.scrollToLocal(this.root, 0, scrollHeight - this.prevHeight - 50);
        this.prevHeight = 0;
      }
    }
  },
  updated() {
    if (!this.preventUpdate) {
      setTimeout(this.updatedTasks, 0);
    }
  },
  watch: {
    goToBottomAction(value) {
      if (value) {
        this.handleGoDown();
      }
    },
    routeAnimating(value) {
      if (!value) {
        this.$nextTick(() => {
          // Root element from where we are supposed to scroll
          this.root = document.querySelector(".slide-section-main");

          // Add scroll event listener
          this.root.addEventListener("scroll", this.scrollBehaviour);

          // Resize event listener
          window.addEventListener("resize", this.resizeHandler);

          // Scroll to the first unread message
          this.scrollToFirstUnreadMessage();
        });
      }
    }
  },
  beforeDestroy() {
    // Remove event listeners before destroying
    if (this.root) {
      this.root.removeEventListener("scroll", this.scrollBehaviour);
    }
    window.removeEventListener("resize", this.resizeHandler);
  },
  components: {
    LoaderVue
  }
};
</script>

<style scoped>
.message-box {
  padding: 0px 20px 0px 20px;
  display: flex;
  flex-direction: column;
  top: 106px;
  position: relative;
}

@-moz-document url-prefix() {
  .message-box {
    padding: 90px 20px 0px 20px;
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;
  }
}

.message-box .all-message-container {
  display: flex;
}

.message-box .all-message-container.user-message-container {
  align-self: flex-end;
}

.message-box .client-message,
.message-box .user-message {
  font-size: 13px;
  padding: 10px 0px;
}

.message-box .user-message {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.message-box .client-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.message-box .client-message p {
  width: 100%;
}

.message-box .user-message p {
  width: 100%;
}

.message-box .user-message.custom p {
  width: auto !important;
  min-width: 81px;
}

.message-content.loader {
  display: flex;
  width: 75vw;
  justify-content: flex-end;
}

.message-content.loader p {
  margin-right: 10px;
}

.message-box div p {
  padding: 10px;
  background-color: #d26d13;
  -webkit-border-radius: 5px;
  -moz-border-radius: 5px;
  border-radius: 5px;
  margin: 0px;
  padding-right: 15px;
  color: #fff;
  max-width: 75vw;
  font-size: 16px;
}

.message-box .user-message p {
  background-color: #dfdbdb;
  color: black;
}

.message-username.client-username {
  font-weight: bold;
  color: gray;
  font-size: 10px;
  position: relative;
  left: 5px;
}

.message-username.user-username {
  font-weight: bold;
  color: gray;
  font-size: 10px;
  position: relative;
  right: 5px;
}

.loader-container-top {
  display: flex;
  width: 100%;
  justify-content: center;
  position: relative;
  top: 110px;
}

.main-unread-container {
  position: fixed;
  right: 40px;
  bottom: 70px;
}

.main-unread-container .unread {
  font-size: 10px;
  font-weight: bold;
  background-color: #d26d13;
  position: relative;
  width: 20px;
  height: 20px;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  left: 40px;
  top: 10px;
}

.arrow-container {
  background-color: white;
  width: 60px;
  height: 60px;
  border-radius: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 0px 10px #f2f2f2;
  cursor: pointer;
}

.arrow-container .down-arrow {
  transform: rotate(90deg);
  width: 30px;
  height: 30px;
}

.loader-small {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #d26d13;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 0.5s linear infinite;
  float: right;
  margin-top: 5px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.zoomIn {
  animation: zoomIn 0.2s ease-in-out;
}

.zoomOut {
  animation: zoomOut 0.5s ease-in-out;
}

.message-chatbox {
  word-break: break-all;
}

.message-box .all-message-container:last-child {
  margin-bottom: 75px;
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale3d(0, 0, 0);
  }

  50% {
    opacity: 0.5;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  to {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
}

@keyframes zoomOut {
  from {
    opacity: 1;
  }

  50% {
    opacity: 0;
    transform: scale3d(0.3, 0.3, 0.3);
  }

  to {
    opacity: 0;
  }
}
</style>
